/**
 * "Foto do passageiro" on the rider record (D725, doc 29 §5 Q2 - owner: "Foto é obrigatória"): the rider's current profile photo and the earlier ones the
 * governed retention still keeps - when each was sent, where it came from (the live camera or, where allowed, the gallery), what the phone's face check said
 * (a photo the phone could not check is flagged for review) and until when it is kept. The IMAGE opens only on demand and only for an operator with the
 * privacy permission (`rider_photo:view_media`); every opening is logged in the account history before the image leaves the server.
 *
 * A photo is rejected under FOUR EYES: an operator with `rider_photo:reject` proposes it with a catalogued reason (and an optional note); ANOTHER operator with
 * `rider_photo:approve` approves (the rider is asked for a new photo) or keeps the photo. The server refuses the proposer's own decision; the card says so.
 */
import { type ReactElement, useState } from 'react';
import { type AdminRiderPhotoView, type DirectoryUser } from '@vello/api-client';
import { Badge, Button, Field, Ink, RecordRow, RecordSection, Select, Stack, Text, TextArea } from '@vello/web-theme';
import { maySelfApprove } from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { PersonCard, useCardRead } from './person-card';
import { usePersonFormat } from './person-format';

const REASON_ID = 'prec-photo-reason';
const NOTE_ID = 'prec-photo-note';

export function PersonRiderPhotoCard({ user }: { user: DirectoryUser }): ReactElement {
  const { runtime, me, grants } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { t } = useT();
  const { v, fail } = useVocab();
  const fmt = usePersonFormat();
  const { state, reload } = useCardRead(() => runtime.riderPhoto.get(user.id), [runtime, user.id]);
  const [rejecting, setRejecting] = useState(false);
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const propose = async (photoId: string): Promise<void> => {
    if (reasonCode === '') return;
    setBusy(true);
    setError(undefined);
    const res = await runtime.riderPhoto.reject(user.id, photoId, { reasonCode, ...(note.trim() !== '' ? { note: note.trim() } : {}) });
    setBusy(false);
    if (!res.ok) {
      setError(fail(res.error));
      return;
    }
    setRejecting(false);
    setReasonCode('');
    setNote('');
    reload();
  };

  const decide = async (rejectionId: string, approve: boolean): Promise<void> => {
    setBusy(true);
    setError(undefined);
    const res = await runtime.riderPhoto.decide(user.id, rejectionId, approve);
    setBusy(false);
    if (!res.ok) setError(fail(res.error));
    else reload();
  };

  return (
    <PersonCard title={t('prec.photo.title')} what={t('prec.what.photo')} state={state} reload={reload} isEmpty={(r) => r.current === null && r.history.length === 0} empty={t('prec.photo.empty')}>
      {(r) => (
        <>
          {r.current !== null && (
            <RecordSection>
              <Text as="h3" role="label">
                {t('prec.photo.current')}
              </Text>
              <Photo userId={user.id} photo={r.current} mediaViewable={r.mediaViewable} />
              {r.pending !== null ? (
                <Stack gap={2} data-testid="prec-photo-pending">
                  <Text as="p" role="caption" tone="warning">
                    {t('prec.photo.pending', { date: fmt.when(r.pending.proposedAt), reason: v('identity.rider_photo_rejection', r.pending.reasonCode) })}
                    {r.pending.note !== undefined ? ` · ${r.pending.note}` : ''}
                  </Text>
                  {!selfApproval && me?.id !== undefined && me.id === r.pending.proposedBy ? (
                    <Text as="p" role="caption" tone="secondary" data-testid="prec-photo-yours">
                      {t('prec.photo.yours')}
                    </Text>
                  ) : (
                    r.canApprove && (
                      <RecordRow dense>
                        <Button variant="secondary" size="sm" disabled={busy} onClick={() => void decide(r.pending?.id as string, true)} data-testid="prec-photo-approve">
                          {t('prec.photo.approve')}
                        </Button>
                        <Button variant="ghost" size="sm" disabled={busy} onClick={() => void decide(r.pending?.id as string, false)} data-testid="prec-photo-decline">
                          {t('prec.photo.decline')}
                        </Button>
                      </RecordRow>
                    )
                  )}
                </Stack>
              ) : (
                r.canReject &&
                (rejecting ? (
                  <Stack gap={2} data-testid="prec-photo-reject-form">
                    <Field label={t('prec.photo.reject.reason')} htmlFor={REASON_ID}>
                      <Select id={REASON_ID} value={reasonCode} onChange={(ev) => setReasonCode(ev.target.value)} required aria-required="true" disabled={busy} data-testid="prec-photo-reason">
                        <option value="">{t('prec.photo.reject.choose')}</option>
                        {r.reasons.map((code) => (
                          <option key={code} value={code}>
                            {v('identity.rider_photo_rejection', code)}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field label={t('prec.photo.reject.note')} htmlFor={NOTE_ID}>
                      <TextArea id={NOTE_ID} value={note} maxLength={500} onChange={(ev) => setNote(ev.target.value)} disabled={busy} data-testid="prec-photo-note" />
                    </Field>
                    <Text as="p" role="caption" tone="secondary">
                      {t('prec.photo.reject.fourEyes')}
                    </Text>
                    <RecordRow dense>
                      <Button variant="secondary" size="sm" disabled={busy || reasonCode === ''} onClick={() => void propose(r.current?.id as string)} data-testid="prec-photo-reject-send">
                        {t('prec.photo.reject.send')}
                      </Button>
                      <Button variant="ghost" size="sm" disabled={busy} onClick={() => setRejecting(false)} data-testid="prec-photo-reject-cancel">
                        {t('prec.photo.reject.cancel')}
                      </Button>
                    </RecordRow>
                  </Stack>
                ) : (
                  <Button variant="secondary" size="sm" onClick={() => setRejecting(true)} data-testid="prec-photo-reject">
                    {t('prec.photo.reject')}
                  </Button>
                ))
              )}
              {error !== undefined && (
                <Text as="p" role="caption" tone="danger" data-testid="prec-photo-error">
                  {error}
                </Text>
              )}
            </RecordSection>
          )}
          {r.history.length > 0 && (
            <RecordSection>
              <Text as="h3" role="label">
                {t('prec.photo.history')}
              </Text>
              {r.history.map((p) => (
                <Photo key={p.id} userId={user.id} photo={p} mediaViewable={r.mediaViewable} />
              ))}
            </RecordSection>
          )}
          {r.rejections.some((x) => x.status !== 'PENDING') && (
            <RecordSection>
              <Text as="h3" role="label">
                {t('prec.photo.rejections')}
              </Text>
              {r.rejections
                .filter((x) => x.status !== 'PENDING')
                .map((x) => (
                  <RecordRow key={x.id} dense>
                    <Ink>{v('identity.rider_photo_rejection', x.reasonCode)}</Ink>
                    <Badge tone={x.status === 'APPROVED' ? 'danger' : 'neutral'}>{v('identity.rider_photo_rejection_status', x.status)}</Badge>
                    <span className="vello-grow" />
                    <Ink mono>{fmt.when(x.decidedAt ?? x.proposedAt)}</Ink>
                  </RecordRow>
                ))}
            </RecordSection>
          )}
        </>
      )}
    </PersonCard>
  );
}

function Photo({ userId, photo: p, mediaViewable }: { userId: string; photo: AdminRiderPhotoView; mediaViewable: boolean }): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { v, fail } = useVocab();
  const fmt = usePersonFormat();
  const [src, setSrc] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const open = async (): Promise<void> => {
    setError(undefined);
    const res = await runtime.riderPhoto.image(userId, p.id);
    if (res.ok) setSrc(`data:${res.value.contentType};base64,${res.value.bytesBase64}`);
    else setError(fail(res.error));
  };
  return (
    <Stack gap={2} data-testid={`prec-photo-${p.id}`}>
      <RecordRow dense>
        <Badge tone={p.status === 'CURRENT' ? 'success' : p.status === 'REJECTED' ? 'danger' : 'neutral'}>{v('identity.rider_photo_status', p.status)}</Badge>
        <Badge tone={p.faceCheck === 'PASSED' ? 'neutral' : 'warning'}>{v('identity.rider_photo_face_check', p.faceCheck)}</Badge>
        <span className="vello-grow" />
        <Ink mono>{t('prec.photo.taken', { date: fmt.when(p.createdAt) })}</Ink>
      </RecordRow>
      <Text as="p" role="caption" tone="secondary">
        {v('identity.rider_photo_source', p.source)} · {t('prec.photo.size', { width: fmt.count(p.width), height: fmt.count(p.height) })}
        {p.purgeAfter !== undefined ? ` · ${t('prec.photo.keptUntil', { date: fmt.when(p.purgeAfter) })}` : ''}
      </Text>
      {!p.held ? (
        <Text as="p" role="caption" tone="secondary">
          {t('prec.photo.notHeld')}
        </Text>
      ) : mediaViewable ? (
        <>
          <RecordRow dense>
            <Button variant="secondary" size="sm" onClick={() => void open()} data-testid={`prec-photo-open-${p.id}`}>
              {t('prec.photo.open')}
            </Button>
          </RecordRow>
          <Text as="p" role="caption" tone="secondary">
            {t('prec.photo.open.logged')}
          </Text>
        </>
      ) : (
        <Text as="p" role="caption" tone="secondary" data-testid={`prec-photo-locked-${p.id}`}>
          {t('prec.photo.locked')}
        </Text>
      )}
      {error !== undefined && (
        <Text as="p" role="caption" tone="danger">
          {error}
        </Text>
      )}
      {src !== undefined && <img className="vello-media-preview" src={src} alt={t('prec.photo.image.alt')} data-testid={`prec-photo-image-${p.id}`} />}
    </Stack>
  );
}
